import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { FloatingWhatsApp } from "@/components/WhatsAppButton";
import Chatbot from "@/components/Chatbot";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/track", label: "Track Order" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function Navbar() {
  const { count } = useCart();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b border-brand-line bg-cream/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          {settings.logo && (
            <img src={settings.logo} alt="logo" className="h-11 w-11 rounded-full object-contain" />
          )}
          <div className="leading-tight">
            <span className="block font-heading text-xl font-semibold text-brand">
              {settings.business_name || "Taanya Homefoods"}
            </span>
            <span className="hidden font-accent text-sm text-brand-green sm:block">Homemade • Made With Love</span>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-brand ${
                  isActive ? "text-brand" : "text-brand-dark"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-search-btn"
            onClick={() => navigate("/shop")}
            className="rounded-full p-2 text-brand-dark transition-colors hover:bg-brand-gold-light md:hidden"
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </button>
          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative rounded-full p-2 text-brand-dark transition-colors hover:bg-brand-gold-light"
          >
            <ShoppingBag className="h-6 w-6" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">
                {count}
              </span>
            )}
          </Link>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="rounded-full p-2 text-brand-dark md:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-brand-line bg-cream md:hidden">
          <div className="flex flex-col px-4 py-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium ${isActive ? "text-brand" : "text-brand-dark"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { settings } = useSettings();
  return (
    <footer className="mt-20 bg-brand-green text-cream">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h3 className="font-heading text-2xl text-white">{settings.business_name || "Taanya Homefoods"}</h3>
          <p className="mt-3 font-accent text-xl text-brand-gold">Homemade • Made With Love</p>
          <p className="mt-4 max-w-xs text-sm text-cream/80">
            Pure, fresh & trusted homemade delicacies crafted with love in small batches.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-gold">Explore</h4>
          <ul className="mt-4 space-y-2 text-sm text-cream/85">
            {links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="transition-colors hover:text-brand-gold">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-gold">Contact</h4>
          <ul className="mt-4 space-y-2 text-sm text-cream/85">
            {settings.phone && <li>Phone: {settings.phone}</li>}
            {settings.email && <li>Email: {settings.email}</li>}
            {settings.address && <li>{settings.address}</li>}
            {settings.timings && <li>{settings.timings}</li>}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-cream/60">
        © {new Date().getFullYear()} {settings.business_name || "Taanya Homefoods"}. All rights reserved.
      </div>
    </footer>
  );
}

export default function CustomerLayout() {
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <Chatbot />
    </div>
  );
}
